import { COUNTER_PAYMENT_METHODS, DOCUMENT_KINDS } from './vocabulary'

/**
 * S10: the printed fallback kit ("Kit di emergenza"). When the server is down
 * the counter keeps renting on paper: a numbered contract, a hand-over and
 * return sheet, the money taken and the CaRGOS report made by hand. S11 types
 * each sheet back in at its real times. This file is the one list of what a
 * sheet asks for: the PDF prints these fields and nothing else, and S11's
 * re-entry form takes these fields and nothing else, so the two cannot drift.
 *
 * The contents are the developer's proposal, to be checked by the owner and
 * the lawyer (DECISIONS, S10; Q-112 for the CaRGOS page). They follow what the
 * system records today: the identity the counter types for CaRGOS (decision
 * 5), the licence, the hand-over and return (km, fuel in eighths, damages by
 * the diagram's zones), the counter's payment methods, and the deposit
 * methods that need no server (the shop's POS hold, or cash).
 */

/** How a field is filled in on paper, and so how the PDF draws it. */
export const PAPER_FIELD_KINDS = [
  /** Free text on a line. */
  'text',
  /** A few lines of free text, in a box. */
  'note',
  /** A day: __/__/____. */
  'date',
  /** A day and a time: __/__/____ __:__. Every real time S11 needs is one. */
  'datetime',
  /** A whole number (km). */
  'number',
  /** Euro and cents: € ______,__. */
  'amount',
  /** The tank or battery in eighths, 0 to 8: nine boxes. */
  'fuel',
  /** One box ticked among the options. */
  'choice',
  /** The damage diagram's zones, one row each, for what is seen there. */
  'zones',
  /** A box to sign in. */
  'signature',
] as const
export type PaperFieldKind = (typeof PAPER_FIELD_KINDS)[number]

/** The sheets of one numbered set, in print order. */
export const PAPER_SHEETS = ['contract', 'handover', 'return', 'payments', 'cargos'] as const
export type PaperSheet = (typeof PAPER_SHEETS)[number]

export interface PaperField {
  readonly key: string
  readonly kind: PaperFieldKind
  /** The boxes of a `choice`, each a code with its own label. */
  readonly options?: readonly string[]
  /** How many times the field repeats on the sheet (the payment lines). */
  readonly rows?: number
}

/**
 * The deposit as the counter can take it with no server: the hold on the
 * shop's own card terminal (`pos`), notes in the till (`cash`), or none (the
 * Owner's call, as at a hand-over today). The smart reader needs the server.
 */
export const PAPER_DEPOSIT_METHODS = ['pos', 'cash', 'none'] as const

/** How a CaRGOS report is made with no server: CaRGOS's own portal, or the PEC (Q-112). */
export const PAPER_CARGOS_CHANNELS = ['portal', 'pec'] as const

/** How many payment lines a set carries: a balance, an extra, a fuel charge, a mistake corrected. */
export const PAPER_PAYMENT_ROWS = 4

/** Every field of every sheet. Keys are stable: S11 stores and validates by them. */
export const PAPER_FIELDS = {
  contract: [
    { key: 'bookingCode', kind: 'text' },
    { key: 'surname', kind: 'text' },
    { key: 'givenNames', kind: 'text' },
    { key: 'birthDate', kind: 'date' },
    { key: 'birthPlace', kind: 'text' },
    { key: 'citizenship', kind: 'text' },
    { key: 'residenceAddress', kind: 'text' },
    { key: 'residencePostalCode', kind: 'text' },
    { key: 'residenceCity', kind: 'text' },
    { key: 'residenceCountry', kind: 'text' },
    { key: 'phone', kind: 'text' },
    { key: 'email', kind: 'text' },
    { key: 'idDocumentKind', kind: 'choice', options: DOCUMENT_KINDS },
    { key: 'idDocumentNumber', kind: 'text' },
    { key: 'idDocumentIssuedBy', kind: 'text' },
    { key: 'licenceNumber', kind: 'text' },
    { key: 'licenceIssuedBy', kind: 'text' },
    { key: 'licenceIssuedOn', kind: 'date' },
    { key: 'licenceExpiresOn', kind: 'date' },
    { key: 'licenceCategories', kind: 'text' },
    { key: 'vehiclePlate', kind: 'text' },
    { key: 'vehicleModel', kind: 'text' },
    { key: 'pickupAt', kind: 'datetime' },
    { key: 'pickupPlace', kind: 'text' },
    { key: 'returnDueAt', kind: 'datetime' },
    { key: 'returnPlace', kind: 'text' },
    { key: 'cover', kind: 'text' },
    { key: 'extras', kind: 'text' },
    { key: 'totalAgreed', kind: 'amount' },
    { key: 'signedAt', kind: 'datetime' },
    { key: 'customerSignature', kind: 'signature' },
    { key: 'specificApprovalSignature', kind: 'signature' },
    { key: 'staffName', kind: 'text' },
  ],
  handover: [
    { key: 'checkedOutAt', kind: 'datetime' },
    { key: 'kmOut', kind: 'number' },
    { key: 'fuelOut', kind: 'fuel' },
    { key: 'damagesOut', kind: 'zones' },
    { key: 'notesOut', kind: 'note' },
    { key: 'staffOut', kind: 'text' },
    { key: 'customerSignatureOut', kind: 'signature' },
  ],
  return: [
    { key: 'returnedAt', kind: 'datetime' },
    { key: 'kmIn', kind: 'number' },
    { key: 'fuelIn', kind: 'fuel' },
    { key: 'damagesIn', kind: 'zones' },
    { key: 'notesIn', kind: 'note' },
    { key: 'staffIn', kind: 'text' },
    { key: 'customerSignatureIn', kind: 'signature' },
  ],
  payments: [
    { key: 'depositAmount', kind: 'amount' },
    { key: 'depositMethod', kind: 'choice', options: PAPER_DEPOSIT_METHODS },
    { key: 'depositTakenAt', kind: 'datetime' },
    { key: 'depositReturnedAt', kind: 'datetime' },
    { key: 'depositKept', kind: 'amount' },
    { key: 'paidAt', kind: 'datetime', rows: PAPER_PAYMENT_ROWS },
    { key: 'paymentAmount', kind: 'amount', rows: PAPER_PAYMENT_ROWS },
    {
      key: 'paymentMethod',
      kind: 'choice',
      options: COUNTER_PAYMENT_METHODS,
      rows: PAPER_PAYMENT_ROWS,
    },
    { key: 'fiscalDocument', kind: 'text', rows: PAPER_PAYMENT_ROWS },
    { key: 'receivedBy', kind: 'text', rows: PAPER_PAYMENT_ROWS },
  ],
  cargos: [
    { key: 'cargosChannel', kind: 'choice', options: PAPER_CARGOS_CHANNELS },
    { key: 'cargosSentAt', kind: 'datetime' },
    { key: 'cargosReceipt', kind: 'text' },
    { key: 'cargosReceiptAt', kind: 'datetime' },
    { key: 'cargosSentBy', kind: 'text' },
  ],
} as const satisfies Record<PaperSheet, readonly PaperField[]>

export type PaperFieldKey = (typeof PAPER_FIELDS)[PaperSheet][number]['key']

/** Every field key, in print order. */
export const PAPER_FIELD_KEYS: readonly PaperFieldKey[] = PAPER_SHEETS.flatMap((sheet) =>
  PAPER_FIELDS[sheet].map((field) => field.key),
)

// ---------------------------------------------------------------------------
// Sheet numbers. One number per set, printed on each of its sheets and typed
// at the hand-over as the paper contract's reference, then again at re-entry.
// It is made without the database, since the kit is printed before the day it
// is needed: the day it was printed, a random kit code and the set's place in
// the kit, then a check character that catches a mistyped or swapped
// character when the number is typed back in.

/** Crockford's base 32: no I, L, O or U, so a number is read aloud without doubt. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const KIT_CODE = /^[0-9A-HJKMNP-TV-Z]{4}$/
const PRINT_DAY = /^\d{4}-\d{2}-\d{2}$/

/** The most sets one kit carries (two-digit sequence). */
export const PAPER_KIT_MAX_SETS = 99

/** A kit code from four random bytes' worth of entropy: four Crockford characters. */
export function paperKitCode(randomBytes: Uint8Array): string {
  if (randomBytes.length < 4) throw new Error('a kit code needs four random bytes')
  return Array.from(randomBytes.subarray(0, 4), (byte) => ALPHABET[byte % 32]).join('')
}

/**
 * Luhn mod 32 over the number's characters: one check character that catches
 * any single wrong character and any two adjacent ones swapped.
 */
function checkCharacter(body: string): string {
  let factor = 2
  let sum = 0
  for (let i = body.length - 1; i >= 0; i--) {
    const value = ALPHABET.indexOf(body[i] ?? '')
    if (value < 0) throw new Error(`not a sheet-number character: ${body[i]}`)
    let addend = factor * value
    factor = factor === 2 ? 1 : 2
    addend = Math.floor(addend / 32) + (addend % 32)
    sum += addend
  }
  return ALPHABET[(32 - (sum % 32)) % 32] ?? '0'
}

/**
 * The number of set `sequence` (1-based) of a kit printed on `day` (its Rome
 * date, yyyy-mm-dd) with code `kit`: `YYMMDD-KKKK-NNC`, e.g. `261101-7Q3F-03K`.
 */
export function paperSheetNumber(day: string, kit: string, sequence: number): string {
  if (!PRINT_DAY.test(day)) throw new Error(`not a day: ${day}`)
  if (!KIT_CODE.test(kit)) throw new Error(`not a kit code: ${kit}`)
  if (!Number.isInteger(sequence) || sequence < 1 || sequence > PAPER_KIT_MAX_SETS) {
    throw new Error(`not a set number: ${sequence}`)
  }
  const date = day.slice(2).replaceAll('-', '')
  const set = String(sequence).padStart(2, '0')
  return `${date}-${kit}-${set}${checkCharacter(`${date}${kit}${set}`)}`
}

export interface ParsedSheetNumber {
  /** As printed, canonical. */
  number: string
  /** The kit's day as printed: yymmdd. */
  printedOn: string
  kit: string
  sequence: number
}

/**
 * A sheet number as someone typed it: any case, spaces or dashes anywhere, the
 * letters O, I and L read as 0, 1 and 1. Null when it is not one, or its check
 * character does not match (a character mistyped).
 */
export function parsePaperSheetNumber(text: string): ParsedSheetNumber | null {
  const plain = text.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1')
  if (!/^\d{6}[0-9A-HJKMNP-TV-Z]{4}\d{2}[0-9A-HJKMNP-TV-Z]$/.test(plain)) return null
  const body = plain.slice(0, 12)
  if (checkCharacter(body) !== plain[12]) return null
  const sequence = Number(plain.slice(10, 12))
  if (sequence < 1) return null
  const printedOn = plain.slice(0, 6)
  const kit = plain.slice(6, 10)
  return {
    number: `${printedOn}-${kit}-${plain.slice(10, 13)}`,
    printedOn,
    kit,
    sequence,
  }
}
